import {
  useEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react";
import styled, { keyframes } from "styled-components";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import videojs from "video.js";
import { Link } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";

import { ClipOfTheDayViewModel } from "./ViewModel";
import { IProPlayerInfo, ITag } from "./@types";
import { IFeaturedItem } from "../Featured/@types";
import { FeaturedContainer } from "../styledComponents";
import { FeaturedUser } from "../FeaturedUser";
import { analyticsClickEvent } from "MVVM/Components/Overlays/utils";
import { OverlayButton } from "MVVM/Components/Overlays";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { analytics } from "MVVM/Analytics";
import background from "MVVM/Assets/Homepage/cs-carousel.webp";
import CotdImage from "../../../../assets/images/feed/COTD_New.svg";

const HERO_STACK_BREAK = "1280px";

const ClipOfTheDay = () => {
  const { clipOfTheDay, proPlayer, tags, loading } = ClipOfTheDayViewModel();

  if (loading)
    return (
      <HeroWrapper style={{ backgroundImage: `url(${background})` }}>
        <FilterWrapper>
          <FeaturedContainer
            justifyContent="center"
            style={{ position: "relative", zIndex: 1 }}
          >
            <SkeletonCard>
              <LoadingSkeleton height="100%" width="100%" />
            </SkeletonCard>
          </FeaturedContainer>
        </FilterWrapper>
      </HeroWrapper>
    );

  if (!clipOfTheDay) return <></>;

  return (
    <HeroWrapper style={{ backgroundImage: `url(${background})` }}>
      <FilterWrapper>
        <FeaturedContainer
          justifyContent="center"
          style={{ position: "relative", zIndex: 1 }}
        >
          <ClipOfTheDayHero
            item={clipOfTheDay}
            proPlayer={proPlayer}
            tags={tags}
          />
        </FeaturedContainer>
      </FilterWrapper>
    </HeroWrapper>
  );
};

const ClipOfTheDayHero = ({
  item,
  proPlayer,
  tags,
}: {
  item: IFeaturedItem;
  proPlayer: IProPlayerInfo | null;
  tags: ITag[];
}) => {
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    analytics.track("Clip of the Day - Impression", {
      type: "homepageHero",
      shareId: item.shareId,
      promotionName: item.title,
    });
  }, [item.shareId, item.title]);

  const analyticsContext = {
    shareId: item.shareId,
    player: proPlayer?.playerName,
    team: proPlayer?.teamName,
    event: proPlayer?.eventName,
  };

  const openClip = () => {
    if (!item.shareId) return;
    analyticsClickEvent(item.type);
    analytics.track("Clip of the Day - Watch", analyticsContext);
    // mute the hero so it doesn't fight the modal clip's audio
    videoPlayerRef.current?.muted(true);
    setMuted(true);
    setClipFullModalState({
      isOpen: ModalType.Clip,
      shareId: item.shareId,
      orientation: "LANDSCAPE",
      contextOverride: {
        type: "default",
        metadata: { feedTitle: "Clip of the Day", source: "modal" },
      },
    });
  };

  const toggleMute = (event: ReactMouseEvent) => {
    event.stopPropagation();
    const next = !muted;
    videoPlayerRef.current?.muted(next);
    setMuted(next);
    analytics.track(
      next ? "Clip of the Day - Mute" : "Clip of the Day - Unmute",
      analyticsContext,
    );
  };

  return (
    <HeroCard className="cotd-hero">
      <VideoPlayerRounder onClick={openClip}>
        <CotdBadge src={CotdImage} aria-hidden />
        <Poster src={item.thumbnailUrl} width="100%" />
        <Box style={{ position: "relative", zIndex: 2, lineHeight: 0 }}>
          <VideoPlayer
            refForVideoPlayer={videoPlayerRef}
            mp4Source={item.url}
            loop={true}
            eventSrc="ClipOfTheDay"
            playAnalyticsProperties={{
              shareId: item.shareId,
              source: "Homepage Clip of the Day",
            }}
            watchAnalyticsProperties={{
              action: "watch",
              collection: "Homepage Clip of the Day",
              collectionType: "Generic",
              item: item.shareId,
              itemType: "Clip",
            }}
            options={{
              aspectRatio: "16:9",
              autoplay: "muted",
              controls: false,
              fill: true,
              fluid: true,
              muted: true,
              playsinline: true,
              poster: item.thumbnailUrl,
              responsive: true,
              userActions: {
                click: false,
              },
            }}
          />
        </Box>
        <MuteToggle onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
          {muted ? <FaVolumeMute size={15} /> : <FaVolumeUp size={15} />}
        </MuteToggle>
      </VideoPlayerRounder>
      {proPlayer ? (
        <ProPlayerPanel
          item={item}
          proPlayer={proPlayer}
          tags={tags}
          onWatch={openClip}
        />
      ) : (
        <DefaultPanel item={item} tags={tags} />
      )}
    </HeroCard>
  );
};

const GameplayBadges = ({ tags }: { tags: ITag[] }) => {
  if (!tags.length) return null;
  return (
    <BadgeRow>
      <ClipBadges tags={tags} size="md" maxVisible={3} expandable />
    </BadgeRow>
  );
};

const DefaultPanel = ({
  item,
  tags,
}: {
  item: IFeaturedItem;
  tags: ITag[];
}) => (
  <HeroSection>
    <Flex flexDirection="column">
      <HeroTitle mb={3}>Clip of the Day</HeroTitle>
      <MobileHide>
        <FeaturedUser
          avatar={item.avatar}
          createdAt={item.createdAt}
          userId={item.userId}
          username={item.username}
          views={item.views}
        />
      </MobileHide>
      <GameplayBadges tags={tags} />
    </Flex>
    <OverlayButton
      buttonCopy="Go To Clip of the Day!"
      link={item.link}
      name={item.title}
      type={item.type}
      style={buttonStyle}
    />
  </HeroSection>
);

const ProPlayerPanel = ({
  item,
  proPlayer,
  tags,
  onWatch,
}: {
  item: IFeaturedItem;
  proPlayer: IProPlayerInfo;
  tags: ITag[];
  onWatch: () => void;
}) => {
  const profileLink = proPlayer.playerGameIdentifier
    ? `/u/${proPlayer.playerGameIdentifier}`
    : undefined;
  const flagUrl = proPlayer.countryISO
    ? `${
        process.env.REACT_APP_S3_CLIP_BUCKET
      }/static/web/flags/${proPlayer.countryISO.toLowerCase()}.png`
    : undefined;
  const clipDate = item.createdAt
    ? dateFormatWithDistanceSuffix(item.createdAt)
    : "";

  return (
    <ProSection>
      {proPlayer.teamLogo && (
        <TeamBackdrop src={proPlayer.teamLogo} aria-hidden />
      )}
      <ProContent>
        {proPlayer.teamName && (
          <Matchup>
            {proPlayer.teamLogo && <MatchupLogo src={proPlayer.teamLogo} />}
            <MatchupTeam title={proPlayer.teamName}>
              {proPlayer.teamName}
            </MatchupTeam>
            {proPlayer.opponentTeamName && (
              <>
                <ScorePill>{proPlayer.scoreLine || "VS"}</ScorePill>
                {proPlayer.opponentTeamLogo && (
                  <MatchupLogo src={proPlayer.opponentTeamLogo} />
                )}
                <MatchupTeam title={proPlayer.opponentTeamName}>
                  {proPlayer.opponentTeamName}
                </MatchupTeam>
              </>
            )}
          </Matchup>
        )}

        {proPlayer.eventName && (
          <EventHeader>
            {proPlayer.eventLogo && (
              <EventHeaderLogo src={proPlayer.eventLogo} />
            )}
            <Flex flexDirection="column" style={{ minWidth: 0 }}>
              <EventHeaderName>{proPlayer.eventName}</EventHeaderName>
              {proPlayer.seriesGameLabel && (
                <EventHeaderSeries>
                  {proPlayer.seriesGameLabel}
                </EventHeaderSeries>
              )}
            </Flex>
          </EventHeader>
        )}

        <Flex style={{ gap: 16, alignItems: "center" }}>
          {proPlayer.avatar &&
            (profileLink ? (
              <Link to={profileLink}>
                <PlayerAvatar src={proPlayer.avatar} />
              </Link>
            ) : (
              <PlayerAvatar src={proPlayer.avatar} />
            ))}
          <Flex flexDirection="column" style={{ minWidth: 0 }}>
            <Flex style={{ gap: 8, alignItems: "center", minWidth: 0 }}>
              {profileLink ? (
                <PlayerNameLink to={profileLink}>
                  {proPlayer.playerName}
                </PlayerNameLink>
              ) : (
                <PlayerName>{proPlayer.playerName}</PlayerName>
              )}
              {flagUrl && <NameFlag src={flagUrl} aria-hidden />}
            </Flex>
            {proPlayer.playerFullName && (
              <PlayerFullName>{proPlayer.playerFullName}</PlayerFullName>
            )}
          </Flex>
        </Flex>

        <GameplayBadges tags={tags} />

        <Spacer />

        {clipDate && <ClipDate>{clipDate}</ClipDate>}

        <ButtonRow>
          <WatchCta type="button" onClick={onWatch}>
            Watch the Play
          </WatchCta>
          {profileLink && (
            <ProfileCta
              to={profileLink}
              onClick={() =>
                analytics.track("Clip of the Day - Profile Click", {
                  player: proPlayer.playerName,
                  playerGameIdentifier: proPlayer.playerGameIdentifier,
                })
              }
            >
              Visit Profile
            </ProfileCta>
          )}
        </ButtonRow>
      </ProContent>
    </ProSection>
  );
};

const buttonStyle = {
  borderRadius: "12px",
  fontWeight: 700,
  lineHeight: "1em",
  position: "static" as const,
  minHeight: "20px",
};

const SkeletonCard = styled(Box)`
  border-radius: 12px;
  height: 440px;
  line-height: 0;
  max-width: 1120px;
  overflow: hidden;
  width: 100%;

  span {
    display: block;
    height: 100%;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    height: 78vw;
    max-height: 620px;
  }
`;

const HeroWrapper = styled(Box)`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 0 -60px -16px -60px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    margin: 0 -22px 0 -22px;
  }
`;

const FilterWrapper = styled(Box)`
  backdrop-filter: blur(8px);
  height: 100%;
  margin: 0;
  padding: 44px 0 0;
  position: relative;

  ::before {
    background: linear-gradient(
      0deg,
      rgb(29 42 52) 0%,
      rgb(27 42 52) 0%,
      rgb(0 0 0 / 38%) 50%
    );
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 0;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    padding: 0;
  }
`;

const HeroCard = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  height: auto;
  margin: 0 auto;
  max-width: 1120px;
  width: 100%;

  @media (max-width: ${HERO_STACK_BREAK}) {
    flex-direction: column;
    width: 100vw;
  }
`;

const VideoPlayerRounder = styled(Box)`
  cursor: pointer;
  flex: 1;
  font-size: 0;
  line-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;

  @media (max-width: ${HERO_STACK_BREAK}) {
    flex: none;
    width: 100%;
  }

  .video-js {
    display: block;
    vertical-align: bottom;
  }

  .video-js,
  .vjs-poster,
  .vjs-tech,
  .vjs-modal-dialog-content,
  .vjs-error-display {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;

    @media (max-width: ${HERO_STACK_BREAK}) {
      border-radius: 0;
    }
  }

  .video-js .vjs-control-bar {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const CotdBadge = styled.img`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 104px;
  z-index: 3;
  pointer-events: none;
`;

const MuteToggle = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  bottom: 14px;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 34px;
  justify-content: center;
  left: 14px;
  position: absolute;
  width: 34px;
  z-index: 4;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

const Poster = styled(Image)`
  position: absolute;
  z-index: 1;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`;

const HeroSection = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  flex-direction: column;
  justify-content: space-between;
  padding: 36px;
  width: 380px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border-top-right-radius: 0;
    padding: 22px;
    width: 100%;
  }
`;

const ProSection = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: hidden;
  position: relative;
  width: 380px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border-top-right-radius: 0;
    width: 100%;
  }
`;

const sheen = keyframes`
  0% { transform: translateX(-40%) rotate(8deg); opacity: 0.0; }
  35% { opacity: 0.16; }
  100% { transform: translateX(160%) rotate(8deg); opacity: 0.0; }
`;

const TeamBackdrop = styled.img`
  position: absolute;
  right: -30px;
  top: 55%;
  transform: translateY(-50%);
  width: 340px;
  height: 340px;
  object-fit: contain;
  opacity: 0.22;
  filter: saturate(1.3);
  pointer-events: none;
  z-index: 0;
`;

const NameFlag = styled.img`
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
`;

const ProContent = styled(Box)`
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(
    115deg,
    rgba(20, 30, 38, 0.35) 0%,
    rgba(20, 30, 38, 0.6) 55%,
    rgba(20, 30, 38, 0.72) 100%
  );

  ::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 60px;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    animation: ${sheen} 6s ease-in-out infinite;
    pointer-events: none;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    position: static;
    padding: 22px;
  }
`;

const EventHeader = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const EventHeaderLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
`;

const EventHeaderName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventHeaderSeries = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PlayerAvatar = styled.img`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const PlayerName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 32px;
  font-weight: 800;
  line-height: 1.05;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlayerNameLink = styled(Link)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 32px;
  font-weight: 800;
  line-height: 1.05;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.proPlayer};
  }
`;

const PlayerFullName = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  font-weight: 500;
  margin-top: 2px;
`;

const Matchup = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const MatchupLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
`;

const MatchupTeam = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  max-width: 150px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ScorePill = styled(Text)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 7px;
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  padding: 4px 11px;
`;

const BadgeRow = styled(Box)`
  margin-top: 18px;
`;

const Spacer = styled(Box)`
  flex: 1;
  min-height: 6px;
`;

const ClipDate = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ButtonRow = styled(Flex)`
  gap: 10px;
`;

const ctaBase = `
  align-items: center;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  justify-content: center;
  letter-spacing: 0.01em;
  text-decoration: none;
  transition: filter 0.15s ease, background 0.15s ease;
`;

const WatchCta = styled.button`
  ${ctaBase}
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  color: ${({ theme }) => theme.colors.darkerNed};

  &:hover {
    filter: brightness(1.08);
  }
`;

const ProfileCta = styled(Link)`
  ${ctaBase}
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    background: rgba(255, 255, 255, 0.13);
  }
`;

const MobileHide = styled(Box)`
  @media (max-width: ${HERO_STACK_BREAK}) {
    display: none;
  }
`;

const HeroTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 800;
  font-size: 32px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    font-size: ${({ theme }) => theme.fontSizes[3]};
    margin-top: -2px;
    margin-bottom: 20px;
    max-width: 70%;
  }
`;

export { ClipOfTheDay };
